import {
    FetchState,
    EmployerRolePermission,
    EmployerRolePermissionState,
    UserRoleModel
} from "@/shared/types";
import * as UserService from "@/shared/api/userService";
import * as storeHelper from "@/shared/storeHelper";
import { defineStore } from "pinia";
import _ from "lodash";
import { useNotifierStore } from "@/shared/store/notifier";

export const initialState: EmployerRolePermissionState = {
    fetchState: FetchState.Uninitialized,
    postFetchState: FetchState.Uninitialized,
    employerRolePermissions: [],
    roles: [],
    rolefetchState: FetchState.Uninitialized,
    role: null,
    singlerolefetchState: FetchState.Uninitialized
};

export const useEmployerRolePermissionStore = defineStore("employerRolePermission", {
    state: (): EmployerRolePermissionState => (_.cloneDeep(initialState)),
    getters: {
        isLoaded: state => state.fetchState === FetchState.Loaded,
        isPosting: state => state.postFetchState === FetchState.Loading,
        allRoles: state => state.roles,
        isRolesLoaded: state => state.rolefetchState === FetchState.Loaded,
        singleRole: state => state.role,
        isRoleLoaded: state => state.singlerolefetchState === FetchState.Loaded
    },
    actions: {
        setGetRequestFetchState (fetchState: FetchState) {
            this.fetchState = fetchState;
        },
        setPostRequestFetchState (fetchState: FetchState) {
            this.postFetchState = fetchState;
        },
        setEmployerRolePermissions (
            employerRolePermissions: EmployerRolePermission[]) {
            this.employerRolePermissions = [ ...employerRolePermissions ];
        },
        setUserRoles (roles: UserRoleModel[]) {
            this.roles = [ ...roles ];
        },
        setUserRolesFetchState (fetchState: FetchState) {
            this.rolefetchState = fetchState;
        },
        setSingleRoleFetchState (fetchState: FetchState) {
            this.singlerolefetchState = fetchState;
        },
        setRole (role: UserRoleModel) {
            this.role = role;
        },
        getRolePermissions () {
            if (this.fetchState !== FetchState.Uninitialized) {
                return Promise.resolve();
            }

            return UserService.getEmployerRolePermissions()
                .then(resp => {
                    this.setEmployerRolePermissions(resp.data);
                    this.setGetRequestFetchState(FetchState.Loaded);
                })
                .catch(err => {
                    storeHelper.handleError({ error: err, rethrow: true });
                });
        },
        getUserManagementRolePermissions (): Promise<void> {
            if (this.fetchState !== FetchState.Uninitialized) {
                return Promise.resolve();
            }

            return UserService.getUserManagementRolePermissions().then(resp => {
                this.setEmployerRolePermissions(resp.data);
                this.setGetRequestFetchState(FetchState.Loaded);
            }).catch(err => {
                storeHelper.handleError({ error: err, rethrow: true });
            });
        },
        saveRolePermissions (
            employerRolePermissions: EmployerRolePermission[]
        ) {
            return new Promise<void>((resolve, reject) => {
                if (this.postFetchState === FetchState.Loading) {
                    resolve();
                    return;
                } else {
                    this.setPostRequestFetchState(FetchState.Loading);
                    return UserService.saveEmployerRolePermissions(employerRolePermissions)
                        .then(resp => {
                            this.setPostRequestFetchState(FetchState.Loaded);
                            const notifierStore = useNotifierStore();
                            notifierStore.setSuccess(
                                `The role permissions have been updated.`,
                            );
                        })
                        .catch(err => {
                            storeHelper.handleError({ error: err, rethrow: true });
                        });
                }
            });
        },
        getRoles () {
            return new Promise<void>((resolve, reject) => {
                if (this.rolefetchState === FetchState.Loading) {
                    resolve();
                    return;
                } else {
                    this.setUserRolesFetchState(FetchState.Loading);
                    return UserService.getUserRoles()
                        .then(resp => {
                            this.setUserRoles(resp.data);
                            this.setUserRolesFetchState(FetchState.Loaded);
                        })
                        .catch(err => {
                            storeHelper.handleError({ error: err, rethrow: true });
                        });
                }
            });
        },
        getRolesByType (roleType: string) {
            return new Promise<void>((resolve, reject) => {
                if (this.rolefetchState === FetchState.Loading) {
                    resolve();
                    return;
                } else {
                    this.setUserRolesFetchState(FetchState.Loading);
                    return UserService.getUserRolesByType(roleType).then(resp => {
                        this.setUserRoles(resp.data);
                        this.setUserRolesFetchState(FetchState.Loaded);
                    }).catch(err => {
                        storeHelper.handleError({ error: err, rethrow: true });
                    });
                }
            });
        },
        createRole (role: UserRoleModel) {
            return UserService.createUserRole(role)
                .then(resp => {
                    return resp;
                })
                .catch(err => {
                    storeHelper.handleError({ error: err, rethrow: true });
                });
        },
        updateRole (role: UserRoleModel) {
            return UserService.updateRole(role)
                .then(resp => {
                    return resp;
                })
                .catch(err => {
                    storeHelper.handleError({ error: err, rethrow: true });
                });
        },
        getRoleById (roleId: number) {
            return new Promise<void>((resolve, reject) => {
                if (this.rolefetchState === FetchState.Loading) {
                    resolve();
                    return;
                } else {
                    this.setSingleRoleFetchState(FetchState.Loading);
                    return UserService.getUserRoleById(roleId).then(resp => {
                        this.setRole(resp.data);
                        this.setSingleRoleFetchState(FetchState.Loaded);
                    }).catch(err => {
                        storeHelper.handleError({ error: err, rethrow: true });
                    });
                }
            });
        },
        updatePermissionsByRole (role: UserRoleModel) {
            return UserService.UpdatePermissionsByRole(role)
                .then(resp => {
                    return resp;
                })
                .catch(err => {
                    storeHelper.handleError({ error: err, rethrow: true });
                });
        },
    }
});